import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Typography, Box, Collapse, Chip, Divider, Button, Avatar } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import SmsIcon from '@mui/icons-material/Sms';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AppointmentEditDialog from './AppointmentEditDialog';
import AppointmentHandoffDialog from './AppointmentHandoffDialog';
import { Appointment, AppointmentStatus, Employee, INTERNAL_APPOINTMENT_TAG, appointmentHasTag } from '../../../../types';
import { formatDate, updateScheduleBitString } from '../../../../time/timeUtils';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updateAppointment, updateAppointmentInEmployeeProfile, cancelAppointment, markAppointmentComplete, getCurrentBusinessConfig } from '../../../../firebase/firebase-delegate';
import { functions } from '../../../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import ConfirmationDialog from '../../../global/ConfirmationDialog';
import LoadingSpinner from '../../../global/LoadingSpinner';
import { dayjs } from '../../../../utils/dayjs-config';
import { Check, EditCalendar, PeopleOutline } from '@mui/icons-material';
import { WEBSITE_ID } from '../../../../projectConfig';
import { isUpcomingStatus } from '../../../../types';
import { useAdminData } from '../../../../contexts/AdminDataContext';
import {
  applyAppointmentChange,
  appointmentsByEmployeeKey,
  employeeScheduleKey,
  ChangeMode,
  removeAppointmentFromEmployeeCaches,
  upsertAppointmentIntoCache,
  type AppointmentsByEmployeeQueryKey,
  type EmployeeScheduleQueryKey,
} from '../../../../utils/cache-delegate';
import { useResultSnackbar } from '../../../global/SuccessOrErrorSnackbar';
import { resolveBookingUxConfig } from '../../../../utils/bookingUxConfig';


interface AppointmentCardProps {
  appointment: Appointment;
  fullWidth?: boolean;
  defaultExpanded?: boolean;
  renderAsDialogContent?: boolean;
  onOpenDetails?: (appointment: Appointment) => void;
  employeeIdOverride?: string;
  scheduleOverride?: Map<string, string>;
  appointmentIdsOverride?: string[];
  onAppointmentChange?: (nextAppointment?: Appointment) => void;
  updateCurrentEmployeeIndexCache?: boolean;
  showSuccess?: (message: string) => void;
  showError?: (message: string) => void;
  onHandoffSuccess: () => void;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  fullWidth = false,
  defaultExpanded = false,
  renderAsDialogContent = false,
  onOpenDetails,
  employeeIdOverride,
  scheduleOverride,
  appointmentIdsOverride,
  onAppointmentChange,
  updateCurrentEmployeeIndexCache = true,
  showSuccess: showSuccessOverride,
  showError: showErrorOverride,
  onHandoffSuccess,
}) => {
  const theme = useTheme();

  const [originalAppointment, setOriginalAppointment] = useState<Appointment>({ ...appointment });
  const [editedAppointment, setEditedAppointment] = useState<Appointment>({ ...appointment });
  const [expanded, setExpanded] = useState(defaultExpanded || renderAsDialogContent);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isHandoffDialogOpen, setHandoffDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [markAppointmentCompleteDialogOpen, setMarkAppointmentCompleteDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);

  const formattedDate = formatDate(originalAppointment.appointmentDate);

  const startTime = dayjs(originalAppointment.appointmentTime, "HHmm");
  const endTime = startTime.add(originalAppointment.appointmentDuration, 'minutes');

  const { currentEmployeeQuery: employeeQuery, employeesQuery, scheduleQuery, refreshAppointmentInCache, appointmentIds } = useAdminData();
  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });
  const queryClient = useQueryClient();
  const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
  const isResourceMode = bookingUxConfig.bookingModel === 'resource';
  const ownerId = businessConfigQuery.data?.paymentInfo?.owner?.trim() || '';
  const currentEmployeeId = employeeQuery.data?.id;
  const handoffEmployees = useMemo(() => {
    const allEmployees = employeesQuery.data ?? [];
    if (!isResourceMode || !ownerId) {
      return allEmployees;
    }
    return allEmployees.filter((employee) => employee.id !== ownerId);
  }, [employeesQuery.data, isResourceMode, ownerId]);
  const effectiveEmployeeId = employeeIdOverride ?? originalAppointment.employeeID ?? currentEmployeeId ?? '';
  const hasMatchingCurrentSchedule =
    Boolean(scheduleQuery.data) &&
    Boolean(currentEmployeeId) &&
    effectiveEmployeeId === currentEmployeeId;
  const effectiveSchedule = scheduleOverride ?? (hasMatchingCurrentSchedule ? scheduleQuery.data : undefined);
  const effectiveAppointmentIds = appointmentIdsOverride ?? appointmentIds;
  const appointmentsQueryKey = useMemo<AppointmentsByEmployeeQueryKey | null>(
    () => (effectiveEmployeeId ? appointmentsByEmployeeKey(effectiveEmployeeId, effectiveAppointmentIds) : null),
    [effectiveEmployeeId, effectiveAppointmentIds]
  );
  const currentEmployeeAppointmentsQueryKey = useMemo<AppointmentsByEmployeeQueryKey | null>(
    () => (currentEmployeeId ? appointmentsByEmployeeKey(currentEmployeeId, appointmentIds) : null),
    [currentEmployeeId, appointmentIds]
  );
  const scheduleQueryKey = useMemo<EmployeeScheduleQueryKey | null>(
    () => (effectiveEmployeeId ? employeeScheduleKey(effectiveEmployeeId) : null),
    [effectiveEmployeeId]
  );
  const currentEmployeeScheduleQueryKey = useMemo<EmployeeScheduleQueryKey | null>(
    () => (currentEmployeeId ? employeeScheduleKey(currentEmployeeId) : null),
    [currentEmployeeId]
  );

  const syncAppointmentFromCache = useCallback(async () => {
    const updated = await refreshAppointmentInCache(appointment.id);
    if (updated) {
      setOriginalAppointment({ ...updated });
      setEditedAppointment({ ...updated });
    }
    return updated ?? null;
  }, [appointment.id, refreshAppointmentInCache]);

  const { showSuccess: showSuccessLocal, showError: showErrorLocal, snackbar } = useResultSnackbar({
    successTitle: 'Schedule Updated!',
    errorTitle: 'Something Went Wrong',
    autoHideDuration: 2500,
    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  });
  const showSuccess = showSuccessOverride ?? showSuccessLocal;
  const showError = showErrorOverride ?? showErrorLocal;

  const updateAppointmentMutation = useMutation({
    mutationFn: async () => {
      await updateAppointment(originalAppointment, editedAppointment, effectiveEmployeeId, effectiveSchedule);
      await updateAppointmentInEmployeeProfile(originalAppointment, editedAppointment);
    },
    onSuccess: async () => {
      setOriginalAppointment({ ...editedAppointment });
      showSuccess('Successfully updated the appointment!');
      const synced = await syncAppointmentFromCache();
      onAppointmentChange?.(synced ?? { ...editedAppointment });
    },
    onError: (error) => {
      console.error('Error updating appointment: ', error);
      showError('Failed to update the appointment. Please try again.');
    },
  });

  const cancelAppointmentMutation = useMutation({
    mutationFn: async () =>
      cancelAppointment(appointment, effectiveEmployeeId, effectiveSchedule ?? undefined),
    onSuccess: async () => {
      const cancelledAppointment = { ...appointment, status: AppointmentStatus.CANCELLED };
      applyAppointmentChange(queryClient, ChangeMode.REPLACE, cancelledAppointment, appointment, {
        siteId: WEBSITE_ID,
        appointmentsQueryKey: appointmentsQueryKey ?? undefined,
        scheduleKey: scheduleQueryKey ?? undefined,
        serverAlreadyUpdatedScheduleBitstrings: true,
        updateCurrentEmployeeIndex: updateCurrentEmployeeIndexCache,
      });
      setOriginalAppointment(cancelledAppointment);
      setEditedAppointment(cancelledAppointment);
      showSuccess('Successfully canceled the appointment!');
      const synced = await syncAppointmentFromCache();
      onAppointmentChange?.(synced ?? cancelledAppointment);
    },
    onError: (error) => {
      console.error('Error canceling appointment: ', error);
      showError('Failed to cancel the appointment. Please try again.');
    },
  });

  const completeAppointmentMutation = useMutation({
    mutationFn: async () => await markAppointmentComplete(appointment, effectiveEmployeeId, effectiveSchedule ?? undefined),
    onSuccess: async () => {
      const completedAppointment = { ...originalAppointment, status: AppointmentStatus.COMPLETE };
      setOriginalAppointment(completedAppointment);
      setEditedAppointment(completedAppointment);
      showSuccess('Successfully marked appointment as complete!');
      const synced = await syncAppointmentFromCache();
      onAppointmentChange?.(synced ?? completedAppointment);
    },
    onError: (error) => {
      console.log('Error completing appointment: ', error);
      showError('Failed to mark appointment as complete. Please try again.');
    },
  });

  const refundPaymentMutation = useMutation({
    mutationFn: async () => {
      const refundFn = httpsCallable(functions, 'refundCustomerPayment');
      const result = await refundFn({ siteId: WEBSITE_ID, appointmentId: originalAppointment.id });
      return result.data as any;
    },
    onSuccess: async (data: any) => {
      const refundedAmount = data?.amountCents ? (data.amountCents / 100).toFixed(2) : null;
      const refundedAppointment: Appointment = {
        ...originalAppointment,
        paid: false,
        paymentInfo: {
          ...(originalAppointment.paymentInfo || {}),
          refunded: true,
          refundedAmountCents: typeof data?.amountCents === 'number' ? data.amountCents : originalAppointment.paymentInfo?.refundedAmountCents,
          refundId: typeof data?.refundId === 'string' ? data.refundId : originalAppointment.paymentInfo?.refundId,
          refundStatus: typeof data?.status === 'string' ? data.status : originalAppointment.paymentInfo?.refundStatus,
          refundedAt: Date.now(),
        },
      };
      setOriginalAppointment(refundedAppointment);
      setEditedAppointment(refundedAppointment);
      showSuccess(refundedAmount ? `Refunded $${refundedAmount} successfully.` : 'Refund issued successfully.');
      const synced = await syncAppointmentFromCache();
      onAppointmentChange?.(synced ?? refundedAppointment);
    },
    onError: (error) => {
      console.error('Error refunding payment: ', error);
      showError(error?.message || 'Failed to refund the payment. Please try again.');
    },
    onSettled: () => setRefundDialogOpen(false),
  });

  const hasOtherEmployees = handoffEmployees.some((employee) => employee.id !== originalAppointment.employeeID);

  const handoffAppointmentMutation = useMutation({
    mutationFn: async (nextEmployee: Employee) => {
      const updatedAppointment: Appointment = {
        ...originalAppointment,
        employeeID: nextEmployee.id,
        employeeName: `${nextEmployee.firstName} ${nextEmployee.lastName}`,
        employeeEmail: nextEmployee.email,
        employeePhoneNumber: nextEmployee.phone,
      };
      await updateAppointment(originalAppointment, updatedAppointment, effectiveEmployeeId, undefined);
      await updateAppointmentInEmployeeProfile(originalAppointment, updatedAppointment);
      return updatedAppointment;
    },
    onSuccess: async (updatedAppointment) => {
      const previousEmployeeId = originalAppointment.employeeID;
      const nextEmployeeId = updatedAppointment.employeeID;
      const removedFromCurrentEmployeeCaches = Boolean(
        currentEmployeeId
        && originalAppointment.employeeID === currentEmployeeId
        && updatedAppointment.employeeID !== currentEmployeeId
      );
      if (removedFromCurrentEmployeeCaches) {
        removeAppointmentFromEmployeeCaches(queryClient, originalAppointment, {
          siteId: WEBSITE_ID,
          appointmentsQueryKey: appointmentsQueryKey ?? undefined,
          scheduleKey: scheduleQueryKey ?? undefined,
          serverAlreadyUpdatedScheduleBitstrings: false,
        });
      }
      const isAssignedToCurrentEmployee = currentEmployeeId
        && originalAppointment.employeeID !== currentEmployeeId
        && updatedAppointment.employeeID === currentEmployeeId;

      upsertAppointmentIntoCache(queryClient, updatedAppointment, WEBSITE_ID);
      if (isAssignedToCurrentEmployee) {
        applyAppointmentChange(queryClient, ChangeMode.ADD, updatedAppointment, undefined, {
          siteId: WEBSITE_ID,
          appointmentsQueryKey: currentEmployeeAppointmentsQueryKey ?? undefined,
          scheduleKey: currentEmployeeScheduleQueryKey ?? undefined,
          serverAlreadyUpdatedScheduleBitstrings: true,
        });
      }
      // Patch any locally cached schedule/list queries for both employees immediately.
      // This keeps cross-employee views current before background refetch finishes.
      const patchScheduleCache = (employeeId: string, patchAppointment: Appointment, cancel: boolean) => {
        queryClient.setQueriesData<Map<string, string> | undefined>(
          { queryKey: ['employeeSchedule', employeeId] },
          (existing) => {
            if (!existing) return existing;
            const currentBitString = existing.get(patchAppointment.appointmentDate);
            if (!currentBitString) return existing;
            const patchedBitString = updateScheduleBitString(
              currentBitString,
              patchAppointment.appointmentTime,
              patchAppointment.appointmentDuration,
              cancel
            );
            if (!patchedBitString) return existing;
            const next = new Map(existing);
            next.set(patchAppointment.appointmentDate, patchedBitString);
            return next;
          }
        );
      };
      const removeFromEmployeeLists = (employeeId: string) => {
        queryClient.setQueriesData<Appointment[] | undefined>(
          { queryKey: ['appointmentsByEmployee', employeeId] },
          (existing) => existing?.filter((entry) => entry.id !== updatedAppointment.id)
        );
        queryClient.setQueriesData<Appointment[] | undefined>(
          { queryKey: ['employeeCalendarAppointments', WEBSITE_ID, employeeId] },
          (existing) => existing?.filter((entry) => entry.id !== updatedAppointment.id)
        );
      };
      const addToEmployeeLists = (employeeId: string) => {
        queryClient.setQueriesData<Appointment[] | undefined>(
          { queryKey: ['appointmentsByEmployee', employeeId] },
          (existing) => {
            if (!existing) return existing;
            const filtered = existing.filter((entry) => entry.id !== updatedAppointment.id);
            return [...filtered, updatedAppointment];
          }
        );
        queryClient.setQueriesData<Appointment[] | undefined>(
          { queryKey: ['employeeCalendarAppointments', WEBSITE_ID, employeeId] },
          (existing) => {
            if (!existing) return existing;
            const filtered = existing.filter((entry) => entry.id !== updatedAppointment.id);
            return [...filtered, updatedAppointment];
          }
        );
      };
      if (previousEmployeeId && !removedFromCurrentEmployeeCaches) {
        patchScheduleCache(previousEmployeeId, originalAppointment, true);
        removeFromEmployeeLists(previousEmployeeId);
      } else if (previousEmployeeId) {
        removeFromEmployeeLists(previousEmployeeId);
      }
      if (nextEmployeeId) {
        patchScheduleCache(nextEmployeeId, updatedAppointment, false);
        addToEmployeeLists(nextEmployeeId);
      }
      // Team/per-employee admin views derive ownership from employees[].appointments.
      // Move the appointment ID between source and destination indexes immediately.
      const moveAppointmentInEmployeeIndex = (
        appointmentsMap: Record<string, string[]> | undefined,
        fromDate: string,
        toDate: string,
        appointmentId: string
      ): Record<string, string[]> => {
        const nextMap = { ...(appointmentsMap ?? {}) };
        const fromIds = (nextMap[fromDate] ?? []).filter((id) => id !== appointmentId);
        if (fromIds.length > 0) {
          nextMap[fromDate] = fromIds;
        } else {
          delete nextMap[fromDate];
        }
        const toIds = nextMap[toDate] ?? [];
        if (!toIds.includes(appointmentId)) {
          nextMap[toDate] = [...toIds, appointmentId];
        }
        return nextMap;
      };
      const removeAppointmentFromEmployeeIndex = (
        appointmentsMap: Record<string, string[]> | undefined,
        dateKey: string,
        appointmentId: string
      ): Record<string, string[]> => {
        const nextMap = { ...(appointmentsMap ?? {}) };
        const nextIds = (nextMap[dateKey] ?? []).filter((id) => id !== appointmentId);
        if (nextIds.length > 0) {
          nextMap[dateKey] = nextIds;
        } else {
          delete nextMap[dateKey];
        }
        return nextMap;
      };
      queryClient.setQueryData<Employee[] | undefined>(['employees'], (existing) => {
        if (!existing) return existing;
        return existing.map((employee) => {
          if (employee.id === previousEmployeeId && previousEmployeeId !== nextEmployeeId) {
            return {
              ...employee,
              appointments: removeAppointmentFromEmployeeIndex(
                employee.appointments,
                originalAppointment.appointmentDate,
                originalAppointment.id
              ),
            };
          }
          if (employee.id === nextEmployeeId) {
            return {
              ...employee,
              appointments: moveAppointmentInEmployeeIndex(
                employee.appointments,
                originalAppointment.appointmentDate,
                updatedAppointment.appointmentDate,
                updatedAppointment.id
              ),
            };
          }
          return employee;
        });
      });
      queryClient.setQueryData<Employee[] | undefined>(['allEmployees'], (existing) => {
        if (!existing) return existing;
        return existing.map((employee) => {
          if (employee.id === previousEmployeeId && previousEmployeeId !== nextEmployeeId) {
            return {
              ...employee,
              appointments: removeAppointmentFromEmployeeIndex(
                employee.appointments,
                originalAppointment.appointmentDate,
                originalAppointment.id
              ),
            };
          }
          if (employee.id === nextEmployeeId) {
            return {
              ...employee,
              appointments: moveAppointmentInEmployeeIndex(
                employee.appointments,
                originalAppointment.appointmentDate,
                updatedAppointment.appointmentDate,
                updatedAppointment.id
              ),
            };
          }
          return employee;
        });
      });
      // Handoff touches two employees. Refresh both source/destination caches so
      // views for non-current employees stay in sync.
      const affectedEmployeeIds = Array.from(new Set([previousEmployeeId, nextEmployeeId].filter(Boolean)));
      affectedEmployeeIds.forEach((employeeId) => {
        queryClient.setQueryData<Map<string, string> | undefined>(
          employeeScheduleKey(employeeId),
          (existing) => (existing ? new Map(existing) : existing)
        );
        queryClient.invalidateQueries({ queryKey: ['employeeSchedule', employeeId] });
        queryClient.invalidateQueries({ queryKey: ['appointmentsByEmployee', employeeId] });
        queryClient.invalidateQueries({ queryKey: ['employeeCalendarAppointments', WEBSITE_ID, employeeId] });
      });
      queryClient.invalidateQueries({ queryKey: ['teamDayAppointments', WEBSITE_ID] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['hydrateAppointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointment'] });

      setOriginalAppointment(updatedAppointment);
      setEditedAppointment(updatedAppointment);
      onHandoffSuccess();
      setHandoffDialogOpen(false);
      const synced = await syncAppointmentFromCache();
      onAppointmentChange?.(synced ?? updatedAppointment);
    },
    onError: (error) => {
      console.error('Error handing off appointment: ', error);
      showError('Failed to hand off the appointment. Please try again.');
    },
  });

  const isLoading = employeeQuery.isLoading || updateAppointmentMutation.isPending || cancelAppointmentMutation.isPending || refundPaymentMutation.isPending || completeAppointmentMutation.isPending;

  const isComplete = originalAppointment.status === 'COMPLETE';
  const isCancelled = originalAppointment.status === 'CANCELLED';
  const isUpcoming = isUpcomingStatus(originalAppointment.status);
  const isAdmin = Boolean(employeeQuery.data?.admin);
  const isCurrentEmployeeAppointment = currentEmployeeId ? originalAppointment.employeeID === currentEmployeeId : true;
  const canManageAppointment = isAdmin || isCurrentEmployeeAppointment;
  const paymentIntentId = (originalAppointment as any)?.stripePaymentIntentId || originalAppointment?.paymentInfo?.paymentIntentId;
  const isPaid = Boolean(originalAppointment?.paid);
  const paidSubtotal = typeof originalAppointment.paymentInfo?.serviceAmountCents === 'number'
    ? originalAppointment.paymentInfo.serviceAmountCents / 100
    : originalAppointment.billingAmount;
  const paidTip = typeof originalAppointment.paymentInfo?.tipAmountCents === 'number'
    ? originalAppointment.paymentInfo.tipAmountCents / 100
    : 0;
  const paidTotal = typeof originalAppointment.paymentInfo?.amountCents === 'number'
    ? originalAppointment.paymentInfo.amountCents / 100
    : paidSubtotal + paidTip;
  const displayAmount = isPaid ? paidTotal : originalAppointment.billingAmount;
  const displayAmountLabel = `$${displayAmount.toFixed(2)}`;
  const canRefund = Boolean(
    isPaid && paymentIntentId && (isAdmin || currentEmployeeId === originalAppointment.employeeID)
  );
  const isInternalBooking = appointmentHasTag(originalAppointment, INTERNAL_APPOINTMENT_TAG);
  const isCollapsible = !renderAsDialogContent;
  const isRefunded = originalAppointment.paymentInfo?.refunded === true;

  const handleToggleExpand = () => {
    if (onOpenDetails) { onOpenDetails(originalAppointment); return; }
    if (!isCollapsible) return;
    setExpanded((prev) => !prev);
  };

  const handlePhoneClick = () => { window.location.href = `tel:${originalAppointment.customerPhoneNumber}`; };
  const handleTextClick = () => { window.location.href = `sms:${originalAppointment.customerPhoneNumber}`; };
  const handleEditClick = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);
  const handleOpenHandoffDialog = () => setHandoffDialogOpen(true);
  const handleCloseHandoffDialog = () => setHandoffDialogOpen(false);
  const handleSaveChanges = () => { updateAppointmentMutation.mutate(); setDialogOpen(false); };
  const handleCancelClick = () => setCancelDialogOpen(true);
  const handleCompleteClick = () => setMarkAppointmentCompleteDialogOpen(true);
  const confirmMarkAppointmentComplete = () => { completeAppointmentMutation.mutate(); setMarkAppointmentCompleteDialogOpen(false); };
  const handleRefundConfirmation = () => { refundPaymentMutation.mutate(); setRefundDialogOpen(false); };
  const confirmCancel = () => { cancelAppointmentMutation.mutate(); setCancelDialogOpen(false); };
  const handleCancelDialogClose = () => setCancelDialogOpen(false);
  const handleConfirmHandoff = (nextEmployee: Employee) => { handoffAppointmentMutation.mutate(nextEmployee); };

  useEffect(() => {
    setExpanded(renderAsDialogContent ? true : defaultExpanded);
  }, [appointment.id, defaultExpanded, renderAsDialogContent]);

  const statusColor = isComplete ? 'success' : isCancelled ? 'error' : 'info';
  const statusLabel = isComplete ? 'Completed' : isCancelled ? 'Cancelled' : 'Upcoming';

  const bodyContent = isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
      <LoadingSpinner />
    </Box>
  ) : (
    <Box display="flex" flexDirection="column" gap={2}>

      {/* ── Top row: time range + amount badge ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTimeIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="h5" component="div">
            {startTime.format("h:mma")} – {endTime.format("h:mma")}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: isCancelled || isComplete ? theme.palette.grey[400] : theme.palette.success.main,
            color: isCancelled || isComplete
              ? theme.palette.getContrastText(theme.palette.grey[400])
              : theme.palette.getContrastText(theme.palette.success.main),
            width: 'auto',
            minWidth: 44,
            height: 44,
            px: 1.25,
            borderRadius: 1.5,
            fontWeight: 700,
            fontSize: '0.9rem',
            lineHeight: 1,
          }}
          aria-label="Total amount"
        >
          {displayAmountLabel}
        </Avatar>
      </Box>

      <Divider sx={{ my: 0.5 }} />

      {/* ── Name row ── */}
      <Box>
        <Typography variant="body1">
          <Box component="span" sx={{ color: 'text.secondary' }}>Booking with: </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>
            {originalAppointment.customerFirstName} {originalAppointment.customerLastName}
          </Box>
        </Typography>
      </Box>

      {/* ── Date and status chips row ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <CalendarTodayIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">{formattedDate}</Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            size="small"
            label={isComplete ? 'Completed' : isCancelled ? 'Cancelled' : 'Upcoming'}
            color={isComplete ? 'success' : isCancelled ? 'error' : 'info'}
            variant="filled"
            sx={(t) => ({ '& .MuiChip-label': { ...t.typography.body2, color: t.palette.common.white } })}
          />
          {isUpcoming && (
            <Chip
              size="small"
              label={originalAppointment.paid ? 'Paid' : 'Not Paid'}
              color={originalAppointment.paid ? 'success' : 'warning'}
              variant="filled"
              sx={(t) => ({ '& .MuiChip-label': { ...t.typography.body2, color: t.palette.common.white } })}
            />
          )}
        </Box>
      </Box>

      {/* ── Services as chips ── */}
      {!!originalAppointment.servicesList?.length && (
        <Box display="flex" gap={1} flexWrap="wrap">
          {originalAppointment.servicesList.map((svc) => (
            <Chip
              key={svc.id}
              label={svc.serviceTitle}
              variant="outlined"
              size="medium"
              sx={(t) => ({
                height: 36,
                '& .MuiChip-label': { ...t.typography.body2, px: 1 },
              })}
            />
          ))}
        </Box>
      )}

      {/* ── Cost breakdown ── */}
      {originalAppointment.paymentInfo && originalAppointment.paid && originalAppointment.paymentInfo.refunded !== true && (
        <Box
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontWeight: 400, letterSpacing: 0.5, textTransform: 'uppercase', mb: 1 }}
          >
            Payment Breakdown
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {typeof originalAppointment.paymentInfo.serviceAmountCents === 'number' && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Services</Typography>
                <Typography variant="body2">${(originalAppointment.paymentInfo.serviceAmountCents / 100).toFixed(2)}</Typography>
              </Box>
            )}
            {typeof originalAppointment.paymentInfo.tipAmountCents === 'number' && originalAppointment.paymentInfo.tipAmountCents > 0 && (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Tip</Typography>
                <Typography variant="body2">${(originalAppointment.paymentInfo.tipAmountCents / 100).toFixed(2)}</Typography>
              </Box>
            )}
            {typeof originalAppointment.paymentInfo.amountCents === 'number' && (
              <>
                <Divider sx={{ my: 0.25 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>Total</Typography>
                  <Typography variant="body2" fontWeight={600}>${(originalAppointment.paymentInfo.amountCents / 100).toFixed(2)}</Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* ── Notes ── */}
      {originalAppointment.notes && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontWeight: 400, letterSpacing: 0.5, textTransform: 'uppercase', mb: 0.75 }}
          >
            Notes
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
            {originalAppointment.notes}
          </Typography>
        </Box>
      )}

      {/* ── Expanded actions ── */}
      <Collapse in={isCollapsible ? expanded : true} timeout="auto" unmountOnExit={isCollapsible}>
        <Box display="flex" flexDirection="column" gap={1.5} mt={0.5}>
          <Divider />

          {/* Contact */}
          {!isInternalBooking && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PhoneIcon />}
                onClick={(e) => { e.stopPropagation(); handlePhoneClick(); }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1, flex: 1, minWidth: 140 }}
              >
                Call
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SmsIcon />}
                onClick={(e) => { e.stopPropagation(); handleTextClick(); }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1, flex: 1, minWidth: 140 }}
              >
                Text
              </Button>
            </Box>
          )}

          {/* Manage: Reschedule + Change Staff */}
          {canManageAppointment && !isComplete && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditCalendar />}
                onClick={(e) => { e.stopPropagation(); handleEditClick(); }}
                disabled={isCancelled}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1, flex: 1, minWidth: 180 }}
              >
                Reschedule
              </Button>
              {!isCancelled && hasOtherEmployees && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PeopleOutline />}
                  onClick={(e) => { e.stopPropagation(); handleOpenHandoffDialog(); }}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1, flex: 1, minWidth: 180 }}
                >
                  Change Staff
                </Button>
              )}
            </Box>
          )}

          {/* Complete + Cancel */}
          {canManageAppointment && isUpcoming && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Button
                variant="contained"
                color="success"
                startIcon={<Check />}
                onClick={(e) => { e.stopPropagation(); handleCompleteClick(); }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1, flex: 1, minWidth: 140 }}
              >
                Complete
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={(e) => { e.stopPropagation(); handleCancelClick(); }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1, flex: 1, minWidth: 140 }}
              >
                Cancel
              </Button>
            </Box>
          )}

          {/* Refund */}
          {canRefund && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={(e) => { e.stopPropagation(); setRefundDialogOpen(true); }}
                disabled={refundPaymentMutation.isPending}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2, py: 1, width: '100%' }}
              >
                Refund Payment
              </Button>
            </Box>
          )}
          {isRefunded && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Typography
                variant="body2"
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: 'text.secondary',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  py: 1,
                  px: 1.5,
                }}
              >
                This has been refunded.
              </Typography>
            </Box>
          )}

          {/* ID footer */}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', fontStyle: 'italic', mt: 0.5 }}
          >
            ID: {originalAppointment.id}
          </Typography>
        </Box>
      </Collapse>

    </Box>
  );

  return (
    <Box display="flex" justifyContent="center">
      <>
        {renderAsDialogContent ? (
          <Box
            onClick={isCollapsible ? handleToggleExpand : undefined}
            sx={{
              width: '100%',
              px: { xs: 1.5, sm: 0.5 },
              pt: { xs: 3, sm: 2 },
              pb: { xs: 1, sm: 0.5 },
              backgroundColor: 'transparent',
              cursor: onOpenDetails || isCollapsible ? 'pointer' : 'default',
            }}
          >
            {bodyContent}
          </Box>
        ) : (
          <Card
            sx={{
              margin: '16px 0',
              padding: '16px',
              backgroundColor: isComplete
                ? theme.palette.grey[100]
                : isCancelled
                  ? theme.palette.action.disabledBackground
                  : theme.palette.background.paper,
              borderRadius: 3,
              border: isCancelled ? 'none' : `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              cursor: onOpenDetails || isCollapsible ? 'pointer' : 'default',
              '&:hover': onOpenDetails || isCollapsible ? { boxShadow: theme.shadows[4], transform: 'translateY(-2px)' } : undefined,
              width: { xs: '100%', md: fullWidth ? '100%' : '50%' },
            }}
            onClick={onOpenDetails || isCollapsible ? handleToggleExpand : undefined}
          >
            <CardContent>
              {bodyContent}
            </CardContent>
          </Card>
        )}

        <AppointmentEditDialog
          isDialogOpen={isDialogOpen}
          editedAppointment={editedAppointment}
          handleCloseDialog={handleCloseDialog}
          handleSaveChanges={handleSaveChanges}
          setAppointment={setEditedAppointment}
          employeeId={effectiveEmployeeId}
          scheduleCache={effectiveSchedule}
        />
        <AppointmentHandoffDialog
          open={isHandoffDialogOpen}
          appointment={originalAppointment}
          employees={handoffEmployees}
          onClose={handleCloseHandoffDialog}
          onConfirm={handleConfirmHandoff}
          isSubmitting={handoffAppointmentMutation.isPending}
        />
      </>

      {snackbar}

      <ConfirmationDialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        title="Cancel Appointment"
        message="Are you sure you want to cancel this appointment?"
        onConfirm={confirmCancel}
      />
      <ConfirmationDialog
        open={markAppointmentCompleteDialogOpen}
        onClose={() => setMarkAppointmentCompleteDialogOpen(false)}
        title="Mark As Complete?"
        message="Are you sure you want to mark this appointment as complete?"
        onConfirm={confirmMarkAppointmentComplete}
      />
      <ConfirmationDialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        title="Refund Payment"
        message="Are you sure you want to refund this payment? This cannot be undone."
        onConfirm={handleRefundConfirmation}
        confirmButtonLabel="Yes, refund"
      />
    </Box>
  );
};

export default AppointmentCard;
